interface KeyValueStore {
  set: (provider: string, clickId: string, expiresInDays: number) => void;
  get: (provider: string) => string | undefined;
}

interface AffiliationSource {
  provider: string;
  clickId: string;
}

interface Tracker {
  key: string;
  getSource: (url: URL) => AffiliationSource;
  shouldBeTracked: (url: URL, keyValueStore: KeyValueStore) => boolean;
  serialise: (source: AffiliationSource) => string;
  deserialise: (value: string | undefined) => AffiliationSource | undefined;
  expireInDays: number;
}

// List of trackers. New trackers can be added in this list.
// Tackers are ordered based on priority:
// tracker with lower index has higher prioirity
const trackers: Tracker[] = [
  {
    key: 'google',
    getSource: (url: URL) => ({
      provider: 'google',
      clickId: url.searchParams.get('gclid') ?? '',
    }),
    shouldBeTracked: (url: URL) =>
      url.searchParams.has('gclid') &&
      url.searchParams.get('utm_source') === 'google',
    serialise: (source: AffiliationSource) => source.clickId,
    deserialise: (value: string) => ({ provider: 'google', clickId: value }),
    expireInDays: 90,
  },
  {
    key: 'display',
    getSource: (url: URL) => ({
      provider: 'display',
      clickId: url.searchParams.get('gclid') ?? '',
    }),
    shouldBeTracked: (url: URL) =>
      url.searchParams.has('gclid') &&
      url.searchParams.get('utm_source') === 'display',
    serialise: (source: AffiliationSource) => source.clickId,
    deserialise: (value: string) => ({ provider: 'display', clickId: value }),
    expireInDays: 90,
  },
  {
    key: 'facebook',
    getSource: (url: URL) => ({
      provider: 'facebook',
      clickId: url.searchParams.get('fbclid') ?? '',
    }),
    shouldBeTracked: (url: URL) => url.searchParams.has('fbclid'),
    serialise: (source: AffiliationSource) => source.clickId,
    deserialise: (value: string) => ({ provider: 'facebook', clickId: value }),
    expireInDays: 90,
  },
  {
    key: 'nestpick',
    getSource: (url: URL) => ({
      provider: 'nestpick',
      clickId: url.searchParams.get('nestpick_transaction_id') ?? '',
    }),
    shouldBeTracked: (url: URL) =>
      url.searchParams.has('nestpick_transaction_id'),
    serialise: (source: AffiliationSource) => source.clickId,
    deserialise: (value: string) => ({ provider: 'nestpick', clickId: value }),
    expireInDays: 30,
  },
  {
    key: 'hometogo',
    getSource: (url: URL) => ({
      provider: 'hometogo',
      clickId: url.searchParams.get('utm_term') ?? '',
    }),
    shouldBeTracked: (url: URL) =>
      url.searchParams.has('utm_term') &&
      url.searchParams.get('utm_source') === 'hometogo',
    serialise: (source: AffiliationSource) => source.clickId,
    deserialise: (value: string) => ({ provider: 'hometogo', clickId: value }),
    expireInDays: 30,
  },
  {
    key: 'affiliate',
    getSource: (url: URL) => ({
      provider: url.searchParams.get('affiliate_partner_id') ?? '',
      clickId: url.searchParams.get('affiliate_click_id') ?? '',
    }),
    shouldBeTracked: (url: URL, kvstore: KeyValueStore) =>
      // we use a very rudimental first touch attribution strategy
      // basically if no attribution is tracked we add attribution
      // othwerwis we ignre it.
      kvstore.get('affiliate') === undefined &&
      url.searchParams.has('affiliate_partner_id') &&
      url.searchParams.has('affiliate_click_id'),

    serialise: (source: AffiliationSource) => JSON.stringify(source),
    deserialise: (value: string) => {
      try {
        return JSON.parse(value);
      } catch {
        // explicitly ignoring parsing errors to avoid crashes
      }
    },
    expireInDays: 30,
  },
];

export function newAffilationTracker(keyValueStore: KeyValueStore) {
  const getSource = (): AffiliationSource | undefined => {
    const sources = trackers
      .map(tracker => {
        const value = keyValueStore.get(tracker.key);

        return value ? tracker.deserialise(value) : undefined;
      })
      .filter(source => source !== undefined);

    return sources[0] as AffiliationSource | undefined;
  };

  const trackSource = (url: URL): void => {
    // on the safe side ignore with invalid location
    if (!url || !url.search) {
      return;
    }

    trackers.forEach((tracker: Tracker) => {
      if (tracker.shouldBeTracked(url, keyValueStore)) {
        keyValueStore.set(
          tracker.key,
          tracker.serialise(tracker.getSource(url)),
          tracker.expireInDays,
        );
      }
    });
  };

  return {
    getSource,
    trackSource,
  };
}
